/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './RowAction';

const RowActionManager: ComponentManagerFn<'RowAction'> = (
  platform: Platform,
): ComponentManager<'RowAction'> => {
  return {
    ...baseComponentManager<'RowAction'>(platform, 'RowAction'),
    type: 'RowAction',
    name: 'Row Action',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultChildren: ['TriggerOnClick'],
    defaultValue,
    props: {
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        helpText: 'Row action menu text',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Select an icon which should be displayed on row selection.',
      },
      iconColor: {
        label: 'Icon Color',
        type: 'ThemeBGColor',
      },
      minRowsSelected: {
        label: 'Min Rows Selected',
        type: 'numberInput',
        helpText:
          'Specify minimum no. of rows to be selected to perform this row action.',
      },
      maxRowsSelected: {
        label: 'Max Rows Selected',
        type: 'numberInput',
        helpText:
          'Specify maximum no. of rows to be selected to perform this row action. Leave blank to set no limit.',
      },
    },
    getDisplayText: (item) => item.props.text.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'play' },
    description: `**Overview**<br/>
The Row Action component defines a specific operation that can be performed on selected rows within a Data Grid.
Unlike a Button Column (which places a button inside every row), Row Actions typically appear in a toolbar or context menu that activates only when one or more rows are selected. This is the standard way to implement bulk operations like "Delete Selected," "Approve All," or "Export to CSV."

**1. Configuration**<br/>
You define how the action appears to the user and identify its purpose.
* **Text:** The label of the action (e.g., "Delete Selected").
* **Expression Support:** You can use an expression to make the text dynamic (e.g., \`"Approve " + $.selectedCount + " Items"\`).
* **Icon:** An optional visual symbol (e.g., Trash, Check) to display alongside the text.
* **Icon Color:** Sets the theme color for the icon (e.g., Error for delete actions).

**2. Selection Logic (Constraints)**<br/>
You can control when this action is available based on how many rows the user has selected. This prevents errors like trying to "Compare" only one item or "Edit" multiple items at once.
* **Min Rows Selected:** The minimum number of rows required to enable this action. (Example: Set to 1 to ensure the user has picked at least something).
* **Max Rows Selected:** The maximum number of rows allowed. (Example: Set to 1 for "Edit" actions since you usually edit one record at a time. Leave blank for unlimited selection e.g., "Delete").

**3. Interaction**<br/>
The action itself does nothing without logic attached.
* **TriggerOnClick:** The component comes with a default **TriggerOnClick** child. You must configure this trigger to define what actually happens (e.g., "Execute Server Action," "Open Page," "Download File") when the user clicks the action.`,
  };
};

export default RowActionManager;
